import classNames from "classnames";
import { ElementType, memo } from "react";
import * as css from "./redo-option-card.module.css";

export type RedoOptionCardLayout = "list" | "grid";

const REVEAL_DELAY_CLASSES = [
  css.optionRevealDelay0,
  css.optionRevealDelay1,
  css.optionRevealDelay2,
  css.optionRevealDelay3,
  css.optionRevealDelay4,
  css.optionRevealDelay5,
  css.optionRevealDelay6,
  css.optionRevealDelay7,
  css.optionRevealDelay8,
  css.optionRevealDelay9,
  css.optionRevealDelay10,
  css.optionRevealDelay11,
] as const;

export interface RedoOptionCardProps {
  label: string;
  /** "list" — full-width card with icon-box + label + radio. "grid" — compact pill. */
  layout: RedoOptionCardLayout;
  selected: boolean;
  Icon?: ElementType<{ className?: string }>;
  badges?: readonly string[];
  disabled?: boolean;
  animateReveal?: boolean;
  revealIndex?: number;
  className?: string;
  onBlur?: () => void;
  onClick: () => void;
  onFocus?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const RedoOptionCard = memo(function RedoOptionCard({
  label,
  layout,
  selected,
  Icon,
  badges,
  disabled,
  animateReveal = false,
  revealIndex = 0,
  className,
  onBlur,
  onClick,
  onFocus,
  onMouseEnter,
  onMouseLeave,
}: RedoOptionCardProps) {
  const buttonClassName = classNames(
    css.option,
    layout === "grid" ? css.optionPill : css.optionCard,
    selected && css.optionSelected,
    animateReveal && css.optionReveal,
    animateReveal &&
      REVEAL_DELAY_CLASSES[
        Math.min(Math.max(0, revealIndex), REVEAL_DELAY_CLASSES.length - 1)
      ],
    className,
  );
  return (
    <button
      aria-pressed={selected}
      className={buttonClassName}
      data-arbiter="RedoOptionCard"
      disabled={disabled}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      type="button"
    >
      {layout === "grid" ? (
        <>
          {Icon ? <Icon className={css.optionPillIcon} /> : null}
          <span className={css.optionPillLabel}>{label}</span>
          {badges?.map((badge) => (
            <span className={css.optionPillBadge} key={badge}>
              {badge}
            </span>
          ))}
        </>
      ) : (
        <>
          <span className={css.optionContent}>
            {Icon ? (
              <span className={css.optionIconBox}>
                <Icon className={css.optionIcon} />
              </span>
            ) : null}
            <span className={css.optionLabel}>{label}</span>
          </span>
          <span className={css.optionRadio}>
            <span className={css.optionRadioInner} />
          </span>
        </>
      )}
    </button>
  );
});
